import { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Employer-facing widget: opt in/out of the public companies directory + talent pool.
 * Mounts inside the employer/recruiter dashboard.
 */
export default function TalentPoolSettings() {
  const [data, setData] = useState({ accepting_talent_pool: false, sector: '', blurb: '' });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/employer/settings/talent-pool`)
      .then((r) => setData({
        accepting_talent_pool: !!r.data.accepting_talent_pool,
        sector: r.data.sector || '',
        blurb: r.data.blurb || '',
      }))
      .catch((err) => setError(err.response?.data?.detail || ''))
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      await axios.put(`${API}/employer/settings/talent-pool`, data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (!loaded) return <div className="h-32 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl animate-pulse" />;

  return (
    <section
      data-testid="talent-pool-settings"
      className="col-span-12 lg:col-span-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden"
    >
      <header className="px-5 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-slate-400" aria-hidden="true" />
        <h3 className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 dark:text-slate-400">
          Talent pool — public directory
        </h3>
      </header>
      <div className="p-5 space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Appear on the public <code className="text-xs">/companies</code> directory. Candidates can opt in to share their AI profile with you;
          identities stay anonymous until you express interest.
        </p>

        <label className="flex items-center justify-between gap-3">
          <span className="text-sm text-slate-900 dark:text-slate-50">Accept talent pool joins</span>
          <button
            onClick={() => setData((d) => ({ ...d, accepting_talent_pool: !d.accepting_talent_pool }))}
            role="switch"
            aria-checked={data.accepting_talent_pool}
            data-testid="accepting-toggle"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${data.accepting_talent_pool ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-700'}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${data.accepting_talent_pool ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </label>

        {data.accepting_talent_pool && (
          <>
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Sector</label>
              <input
                value={data.sector}
                onChange={(e) => setData({ ...data, sector: e.target.value })}
                placeholder="e.g. Investment Banking, Fintech, Insurance"
                data-testid="sector-input"
                maxLength={80}
                className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                Short pitch ({data.blurb.length}/400)
              </label>
              <textarea
                rows="3"
                value={data.blurb}
                onChange={(e) => setData({ ...data, blurb: e.target.value.slice(0, 400) })}
                placeholder="Why should candidates join your talent pool? What's your hiring focus?"
                data-testid="blurb-input"
                className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm"
              />
            </div>
          </>
        )}

        {error && (
          <div className="flex gap-2 items-start text-xs text-rose-600 dark:text-rose-400" data-testid="settings-error">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5" /> {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          {saved && <span className="text-xs text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-settings-btn"
            className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 transition"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </section>
  );
}
